import React, { useState, useEffect } from 'react';
import { login } from '../../services/auth.service';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import LanguageIcon from '@mui/icons-material/Language';
import headerImage from '../../assets/setup_header.jpg';

export const PENDING_CREW_KEY = 'wayfinder:pendingCrew';

export interface PendingCrewSetup {
  name: string;
  description?: string;
  website?: string;
}

interface SlugInfo {
  slug: string;
  available: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

// ─── Step 1: Crew info form ──────────────────────────────────────────────────

interface Step1Props {
  name: string;
  setName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  website: string;
  setWebsite: (v: string) => void;
  slugInfo: SlugInfo | null;
  checking: boolean;
}

function Step1({ name, setName, description, setDescription, website, setWebsite, slugInfo, checking }: Step1Props) {
    const showSlugStatus = name.trim().length > 0;

    const slugAdornment = () => {
        if (!showSlugStatus) return null;
        if (checking) return <CircularProgress size={16} />;
        if (!slugInfo) return null;
        return slugInfo.available
            ? <CheckCircleIcon fontSize="small" color="success" />
            : <ErrorIcon fontSize="small" color="error" />;
    };

    return (
        <Stack spacing={3} sx={{ mt: 1 }}>
            <Box>
                <TextField
                    fullWidth
                    required
                    autoFocus
                    label="Crew Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Acme Corp, Engineering Team"
                    inputProps={{ maxLength: 80 }}
                />
                {showSlugStatus && (
                    <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
                        {slugAdornment()}
                        {slugInfo && (
                            <Typography variant="caption" color={slugInfo.available ? 'success.main' : 'error.main'}>
                                {slugInfo.available
                                    ? `Available · /c/${slugInfo.slug}`
                                    : `"${slugInfo.slug}" is already taken`}
                            </Typography>
                        )}
                        {checking && (
                            <Typography variant="caption" color="text.secondary">Checking availability…</Typography>
                        )}
                    </Box>
                )}
            </Box>

            <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does your crew do?"
                multiline
                rows={2}
                inputProps={{ maxLength: 300 }}
                helperText="Optional · you can change it later"
            />

            <TextField
                fullWidth
                label="Website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://yourcompany.com"
                helperText="Optional · we'll use this later to set up your branding automatically"
                InputProps={{
                    startAdornment: <LanguageIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                }}
            />
        </Stack>
    );
}

// ─── Step 2: Auth prompt ─────────────────────────────────────────────────────

interface Step2Props {
  name: string;
  slug: string;
  description: string;
  website: string;
}

function Step2({ name, slug, description, website }: Step2Props) {
    return (
        <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="body1" color="text.secondary">
        Your crew is ready to be set up. Create a free account or sign in to launch it.
            </Typography>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                    <Stack spacing={0.5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h6" fontWeight={700}>{name}</Typography>
                            <Chip label={`/c/${slug}`} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                        </Box>
                        {description && (
                            <Typography variant="body2" color="text.secondary">{description}</Typography>
                        )}
                        {website && (
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LanguageIcon fontSize="inherit" />
                                {website}
                            </Typography>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            <Divider />

            <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
          Free for teams up to 10 · No credit card required · Upgrade anytime
                </Typography>
            </Stack>
        </Stack>
    );
}

// ─── Main dialog ─────────────────────────────────────────────────────────────

export function PreAuthCrewSetupDialog({ open, onClose }: Props) {
    const [step, setStep] = useState(0);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [website, setWebsite] = useState('');
    const [slugInfo, setSlugInfo] = useState<SlugInfo | null>(null);
    const [checking, setChecking] = useState(false);

    // Reset state whenever dialog opens
    useEffect(() => {
        if (open) {
            setStep(0);
            setName('');
            setDescription('');
            setWebsite('');
            setSlugInfo(null);
            setChecking(false);
        }
    }, [open]);

    // Debounced slug availability check
    useEffect(() => {
        const trimmed = name.trim();
        if (!trimmed) {
            setSlugInfo(null);
            setChecking(false);
            return;
        }
        setChecking(true);
        const timer = setTimeout(async () => {
            try {
                const res = await fetch(`/api/c/check-slug?name=${encodeURIComponent(trimmed)}`);
                const data = await res.json();
                setSlugInfo(data?.data ?? null);
            } catch {
                setSlugInfo(null);
            } finally {
                setChecking(false);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [name]);

    const canProceedStep1 = name.trim().length > 0 && !checking && slugInfo?.available === true;

    const handleSignIn = () => {
        const pending: PendingCrewSetup = {
            name: name.trim(),
            description: description.trim() || undefined,
            website: website.trim() || undefined
        };
        localStorage.setItem(PENDING_CREW_KEY, JSON.stringify(pending));
        login();
    };

    const titles = ['Set up your Crew', 'Create your account'];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <Box component="img" src={headerImage} alt="Preparing to Launch Your Crew" sx={{ width: '100%' }} />

            <DialogTitle>{titles[step]}</DialogTitle>

            <DialogContent>
                {step === 0 ? (
                    <Step1
                        name={name} setName={setName}
                        description={description} setDescription={setDescription}
                        website={website} setWebsite={setWebsite}
                        slugInfo={slugInfo} checking={checking}
                    />
                ) : (
                    <Step2
                        name={name.trim()}
                        slug={slugInfo?.slug ?? ''}
                        description={description.trim()}
                        website={website.trim()}
                    />
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                {step === 0 ? (
                    <>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={() => setStep(1)}
                            disabled={!canProceedStep1}
                        >
              Next
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={() => setStep(0)}>Back</Button>
                        <Button variant="contained" onClick={handleSignIn}>
              Continue to sign in
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
